/**
 * Eval-session seed fixture resolution (gh #3090).
 *
 * A scenario / journey may declare a named `fixture` (+ its `target_base`, the Rekor
 * base to seed it against) — the Rekor fixture its agent turn mutates. `run-eval` owns the session
 * lifecycle, so it RESETS the fixture before the session and CLEARS it after
 * (try/finally), making a data-mutating eval repeatable and abort-safe. This
 * module is the pure, RN-safe resolver that collapses a scenario SET's per-scenario
 * declarations to the single fixture the session seeds — the fixture is a property
 * of the shared base the set writes to, not of one scenario.
 *
 * Set-level semantics are a runtime rule: all enabled scenarios in a set that
 * declare a fixture MUST agree on one (and on one `target_base`); 2+ distinct is
 * an authoring error (mixing bases in one set), rejected before any run. A journey
 * owns its managed set, so its fixture is read directly from the journey row and
 * does not go through this resolver.
 *
 * Seeding/reset of the underlying fixture is performed against Rekor's fixture
 * primitive (wayai-pro/rekor #605); this module only decides WHICH fixture.
 */

/** One scenario's declared seed dependency (empty/whitespace fixture ⇒ none). */
export interface ScenarioFixtureDeclaration {
  fixture?: string | null;
  target_base?: string | null;
}

/** The single fixture (+ the base it seeds) a session seeds. */
export interface ResolvedEvalFixture {
  fixture: string;
  /**
   * The Rekor base the fixture is seeded against. null here when no scenario declared
   * one; the run-time resolver then aborts with `fixture_seed_failed` (the hub seed
   * connection supplies the API origin + token, so the base must ride the eval config).
   */
  target_base: string | null;
}

/** Enabled scenarios in the set disagree on the fixture (or its target_base). */
export interface EvalFixtureMismatch {
  error: 'fixture_mismatch';
  /** The distinct declared values, for a clear operator message. */
  fixtures: string[];
  target_bases: string[];
}

function normalize(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve the single fixture a scenario SET depends on. Returns:
 * - `null` when no enabled scenario declares a fixture (no seeding — today's behavior),
 * - a `ResolvedEvalFixture` when they agree on exactly one,
 * - an `EvalFixtureMismatch` when 2+ distinct fixtures (or target_bases) are declared.
 *
 * `target_base` is only meaningful alongside a fixture: it is resolved from the
 * scenarios that declare the winning fixture, and must itself be single-valued.
 */
export function resolveSetFixture(
  scenarios: ScenarioFixtureDeclaration[],
): ResolvedEvalFixture | EvalFixtureMismatch | null {
  const declared = scenarios
    .map((s) => ({ fixture: normalize(s.fixture), target_base: normalize(s.target_base) }))
    .filter((s): s is { fixture: string; target_base: string | null } => s.fixture !== null);

  if (declared.length === 0) return null;

  const fixtures = [...new Set(declared.map((s) => s.fixture))];
  const targetBases = [...new Set(declared.map((s) => s.target_base).filter((b): b is string => b !== null))];

  if (fixtures.length > 1 || targetBases.length > 1) {
    return { error: 'fixture_mismatch', fixtures, target_bases: targetBases };
  }

  return { fixture: fixtures[0], target_base: targetBases[0] ?? null };
}

/** Type guard: the resolution rejected the set for declaring conflicting fixtures. */
export function isEvalFixtureMismatch(
  value: ResolvedEvalFixture | EvalFixtureMismatch | null,
): value is EvalFixtureMismatch {
  return value !== null && 'error' in value;
}
